import type { CampaignStateUpdate, DashboardCampaign } from "@adpilot/shared";
import {
  IconArchive,
  IconChevronDown,
  IconCircleDot,
  IconCircleX,
  IconPlayerPause,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { getApiErrorMessage } from "@/lib/api-error";
import { updateCampaignState } from "@/lib/campaign-api";
import { cn } from "@/lib/utils";

type CampaignState = CampaignStateUpdate["state"];

const STATE_ACTIONS = [
  { state: "paused", icon: IconPlayerPause },
  { state: "enabled", icon: IconPlayerPlay },
  { state: "archived", icon: IconArchive },
] as const;

export function isCampaignStateActionDisabled(current: CampaignState, target: CampaignState) {
  return current === target || current === "archived";
}

interface CampaignStateMenuProps {
  campaign: Pick<DashboardCampaign, "id" | "name" | "campaignType" | "status">;
  canWrite: boolean;
}

export function CampaignStateMenu({ campaign, canWrite }: CampaignStateMenuProps) {
  const { t } = useTranslation("campaign");
  const queryClient = useQueryClient();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const mutation = useMutation({
    mutationFn: (state: CampaignState) => updateCampaignState(campaign.id, state),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["dashboard", "summary"] }),
        queryClient.invalidateQueries({ queryKey: ["campaigns", "analytics"] }),
        queryClient.invalidateQueries({ queryKey: ["campaign", campaign.id] }),
      ]);
      toast.success(t(`list.stateMenu.success.${result.state}`));
    },
  });
  const supported = campaign.campaignType === "sp";
  const triggerDisabled = mutation.isPending || !canWrite || !supported;
  const triggerTitle = !supported
    ? t("list.stateMenu.spOnly")
    : !canWrite
      ? t("list.stateMenu.permission")
      : undefined;

  async function submitState(state: CampaignState) {
    try {
      await mutation.mutateAsync(state);
      if (state === "archived") setArchiveOpen(false);
    } catch (error) {
      toast.error(await getApiErrorMessage(error));
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="xs"
              className="h-7 justify-start gap-1 px-1"
              disabled={triggerDisabled}
              aria-label={t("list.stateMenu.triggerAria", { name: campaign.name })}
              title={triggerTitle}
            />
          }
        >
          {mutation.isPending ? (
            <Spinner data-icon="inline-start" />
          ) : (
            <CampaignStateIcon state={campaign.status} />
          )}
          <IconChevronDown data-icon="inline-end" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="w-28">
          <DropdownMenuGroup>
            {STATE_ACTIONS.map(({ state, icon: Icon }) => (
              <DropdownMenuItem
                key={state}
                variant={state === "archived" ? "destructive" : "default"}
                disabled={
                  mutation.isPending || isCampaignStateActionDisabled(campaign.status, state)
                }
                onClick={() => {
                  if (state === "archived") setArchiveOpen(true);
                  else void submitState(state);
                }}
              >
                <Icon />
                {t(`list.stateMenu.actions.${state}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("list.stateMenu.archive.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("list.stateMenu.archive.description", { name: campaign.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" disabled={mutation.isPending} />}>
              {t("list.stateMenu.archive.cancel")}
            </AlertDialogClose>
            <Button
              variant="destructive"
              disabled={mutation.isPending}
              onClick={() => void submitState("archived")}
            >
              {mutation.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : (
                <IconArchive data-icon="inline-start" />
              )}
              {t("list.stateMenu.archive.confirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

function CampaignStateIcon({ state }: { state: CampaignState }) {
  const Icon =
    state === "enabled" ? IconCircleDot : state === "paused" ? IconPlayerPause : IconCircleX;
  return (
    <span
      className={cn(
        "flex items-center",
        state === "enabled"
          ? "text-green-11"
          : state === "paused"
            ? "text-amber-11"
            : "text-muted-foreground",
      )}
      aria-hidden="true"
    >
      <Icon />
    </span>
  );
}
